import costCenterEndpoints from '@/lib/api/endpoints/costCenterEndpoints';
import type { CostCenter } from '@/lib/models/costCenter';
import type { ODataResponse } from '@/lib/models/common';

/**
 * Interfaccia per il service dei CostCenters
 * Wrapper che estrae automaticamente i dati dalle response Axios
 */
export interface CostCentersService {
	/**
	 * Recupera la lista dei centri di costo con query OData
	 * @param query - Query string OData (es: "?$top=25&$count=true&$orderby=name asc")
	 * @returns Promise con response OData contenente array di CostCenter
	 */
	getCostCenters(query: string): Promise<ODataResponse<CostCenter>>;

	/**
	 * Recupera un singolo centro di costo per ID
	 * @param id - ID del centro di costo
	 * @returns Promise con singolo CostCenter
	 */
	getCostCenter(id: number): Promise<CostCenter>;

	/**
	 * Aggiorna un centro di costo esistente
	 * @param id - ID del centro di costo da aggiornare
	 * @param payload - Dati da aggiornare
	 * @returns Promise con CostCenter aggiornato
	 */
	updateCostCenter(id: number, payload: CostCenter): Promise<CostCenter>;

	/**
	 * Crea un nuovo centro di costo
	 * @param payload - Dati del nuovo centro di costo
	 * @returns Promise con CostCenter creato
	 */
	postCostCenter(payload: CostCenter): Promise<CostCenter>;

	/**
	 * Elimina un centro di costo
	 * @param id - ID del centro di costo da eliminare
	 * @returns Promise void
	 */
	deleteCostCenter(id: number): Promise<void>;
}

/**
 * Implementazione del service per i CostCenters
 * Estrae automaticamente la proprietà .data dalle response Axios
 */
const costCentersService: CostCentersService = {
	async getCostCenters(query: string): Promise<ODataResponse<CostCenter>> {
		return (await costCenterEndpoints.getCostCenters(query)).data;
	},

	async getCostCenter(id: number): Promise<CostCenter> {
		return (await costCenterEndpoints.getCostCenter(id)).data;
	},

	async updateCostCenter(id: number, payload: CostCenter): Promise<CostCenter> {
		return (await costCenterEndpoints.updateCostCenter(id, payload)).data;
	},

	async postCostCenter(payload: CostCenter): Promise<CostCenter> {
		return (await costCenterEndpoints.postCostCenter(payload)).data;
	},

	async deleteCostCenter(id: number): Promise<void> {
		return (await costCenterEndpoints.deleteCostCenter(id)).data;
	},
};

export default costCentersService;
